import React, { useEffect, useState } from "react";
import ReactPlayer from "react-player";
import IframeVideoEmbed from "./IframeVideoEmbed";
import PinterestPinEmbed from "./PinterestPinEmbed";
import VideoPlaybackFallback from "./VideoPlaybackFallback";
import VideoSkeletonLoader from "./VideoSkeletonLoader";
import { parseVideoUrl } from "../../utils/videoUrl";

interface PropertyVideoPlayerProps {
  url: string;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}

const baseStyle: React.CSSProperties = {
  minHeight: "350px",
  border: "none",
};

const PropertyVideoPlayer: React.FC<PropertyVideoPlayerProps> = ({
  url,
  title,
  className = "w-100 h-100 rounded-4",
  style,
}) => {
  const parsed = parseVideoUrl(url);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setHasError(false);
    setIsLoading(true);
  }, [url]);

  if (!parsed) return null;

  if (hasError) {
    return (
      <VideoPlaybackFallback
        url={parsed.originalUrl}
        className={className}
        style={style}
      />
    );
  }

  const handleError = () => setHasError(true);

  if (parsed.kind === "pinterest") {
    return (
      <PinterestPinEmbed
        url={parsed.originalUrl}
        pinId={parsed.pinterestPinId}
        title={title}
        className={className}
        style={style}
        onError={handleError}
      />
    );
  }

  if (parsed.kind === "iframe") {
    return (
      <IframeVideoEmbed
        src={parsed.src}
        title={title}
        className={className}
        style={style}
        onError={handleError}
      />
    );
  }

  return (
    <div
      className={`position-relative ${className}`}
      style={{ ...baseStyle, ...style, overflow: "hidden" }}
    >
      {isLoading && (
        <VideoSkeletonLoader
          className={`${className} position-absolute top-0 start-0`}
          style={style}
        />
      )}
      <ReactPlayer
        src={parsed.src}
        controls
        width="100%"
        height="100%"
        style={{
          minHeight: "350px",
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.25s ease",
        }}
        onReady={() => setIsLoading(false)}
        onError={handleError}
      />
    </div>
  );
};

export default PropertyVideoPlayer;
